import React, { useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  LinearProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const OS_SECTIONS = [
  {
    title: "Linux",
    items: [
      { label: "Compress a file", code: "gzip filename.txt" },
      { label: "Keep original file", code: "gzip -k filename.txt" },
      { label: "Decompress", code: "gunzip filename.txt.gz" },
      { label: "View without extracting", code: "zcat filename.txt.gz" },
    ],
  },
  {
    title: "macOS",
    items: [
      { label: "Compress", code: "gzip filename.txt" },
      { label: "Keep original", code: "gzip -k filename.txt" },
      { label: "Decompress", code: "gunzip filename.txt.gz" },
    ],
    note: "GZIP is pre-installed on macOS.",
  },
  {
    title: "Windows",
    items: [
      { label: "Using Git Bash", code: "gzip filename.txt" },
      { label: "Using WSL", code: "gzip filename.txt" },
      {
        label: "Using Python",
        code: `import gzip
import shutil

input_file = "file.txt"
output_file = "file.txt.gz"

with open(input_file, "rb") as f_in:
    with gzip.open(output_file, "wb") as f_out:
        shutil.copyfileobj(f_in, f_out)`,
      },
    ],
  },
];

const CopyableCodeBlock = ({ code, onCopy, copied }) => (
  <Paper
    variant="outlined"
    sx={{
      position: "relative",
      p: 1.25,
      mt: 0.5,
      borderRadius: 1.5,
      backgroundColor: "#0b1b3a",
      color: "#f8fafc",
      overflowX: "auto",
    }}
  >
    <IconButton
      size="small"
      onClick={() => onCopy(code)}
      sx={{
        position: "absolute",
        top: 6,
        right: 6,
        color: copied ? "#22c55e" : "#f1f5f9",
        bgcolor: "rgba(255,255,255,0.12)",
        "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
      }}
    >
      {copied ? <CheckIcon sx={{ fontSize: 16 }} /> : <ContentCopyIcon sx={{ fontSize: 16 }} />}
    </IconButton>
    <Typography
      component="pre"
      sx={{
        m: 0,
        pr: 4,
        whiteSpace: "pre",
        color: "#f8fafc",
        opacity: 1,
        fontWeight: 500,
        letterSpacing: 0.2,
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace',
        fontSize: "0.75rem",
        lineHeight: 1.45,
      }}
    >
      {code}
    </Typography>
  </Paper>
);

const UploadOfflineCurModal = ({
  open,
  onClose,
  onSubmit,
  isUploading,
  uploadProgress,
  onRequestCancelUpload,
  accountNumbers = [],
}) => {
  const now = new Date();
  const [accountNumber, setAccountNumber] = useState("");
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [selectedFile, setSelectedFile] = useState(null);
  const [formError, setFormError] = useState("");
  const [copiedCode, setCopiedCode] = useState("");
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);

  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return [currentYear + 1, currentYear, currentYear - 1, currentYear - 2];
  }, []);

  const accountNumberOptions = useMemo(() => {
    if (!Array.isArray(accountNumbers)) return [];
    const seen = new Set();
    const list = [];
    accountNumbers.forEach((x) => {
      const s = x != null ? String(x).trim() : "";
      if (s && !seen.has(s)) {
        seen.add(s);
        list.push(s);
      }
    });
    return list.sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  }, [accountNumbers]);

  const normalizedAccountNumber = accountNumber.trim();
  const isValidAccountNumber = /^\d{12}$/.test(normalizedAccountNumber);

  const isFormComplete =
    isValidAccountNumber && Boolean(year) && Boolean(month) && Boolean(selectedFile);

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      setSelectedFile(null);
      return;
    }
    setFormError("");
    setSelectedFile(file);
  };

  const resetModalState = () => {
    setAccountNumber("");
    setYear(now.getFullYear());
    setMonth(now.getMonth() + 1);
    setSelectedFile(null);
    setFormError("");
    setCopiedCode("");
    setCopySnackbarOpen(false);
  };

  const handleCopyCode = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedCode(text);
      setCopySnackbarOpen(true);
      setTimeout(() => setCopiedCode(""), 1400);
    } catch (error) {
      setCopySnackbarOpen(false);
    }
  };

  const finishClose = () => {
    resetModalState();
    onClose();
  };

  const handleClose = () => {
    if (isUploading) {
      onRequestCancelUpload?.();
      return;
    }
    finishClose();
  };

  const handleSubmit = async () => {
    if (!normalizedAccountNumber) {
      setFormError("Account number is required.");
      return;
    }
    if (!isValidAccountNumber) {
      setFormError("Account number must be exactly 12 digits.");
      return;
    }
    if (!selectedFile) {
      setFormError("Please select a file to upload.");
      return;
    }
    const name = selectedFile.name.toLowerCase();
    if (!name.endsWith(".gz")) {
      setFormError("Only gzip files (.gz) are supported.");
      return;
    }

    setFormError("");
    const success = await onSubmit({
      accountNumber: normalizedAccountNumber,
      year,
      month,
      file: selectedFile,
    });

    if (success) {
      finishClose();
    }
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={() => {
        if (isUploading) {
          onRequestCancelUpload?.();
          return;
        }
        finishClose();
      }}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 2, mr: { md: "460px" } } }}
    >
      <DialogTitle sx={{ pr: 6, fontWeight: 600 }}>
        Upload Offline CUR File
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{ position: "absolute", right: 12, top: 12 }}
          disabled={isUploading}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {formError && <Alert severity="error">{formError}</Alert>}
          <Alert
            severity="info"
            sx={{
              fontSize: "0.8125rem",
              "& .MuiAlert-message": { width: "100%" },
            }}
          >
            <Box component="span" sx={{ display: "block" }}>
              Upload one .gz file for the selected account and billing period (UTC).
            </Box>
            <Box component="span" sx={{ display: "block", mt: 1.25 }}>
              If you are uploading another CUR file for the same month, the file name should differ from
              your previous upload or it will override your previous file.
            </Box>
          </Alert>
          <Autocomplete
            freeSolo
            size="small"
            options={accountNumberOptions}
            inputValue={accountNumber}
            onInputChange={(event, newInputValue) =>
              setAccountNumber((newInputValue ?? "").replace(/\D/g, "").slice(0, 12))
            }
            fullWidth
            renderInput={(params) => (
              <TextField
                {...params}
                label="Account number"
                placeholder="Type or select an account"
                error={Boolean(normalizedAccountNumber) && !isValidAccountNumber}
                helperText={
                  Boolean(normalizedAccountNumber) && !isValidAccountNumber
                    ? "Account number must be exactly 12 digits."
                    : ""
                }
              />
            )}
          />
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <FormControl size="small" fullWidth>
              <InputLabel>Month</InputLabel>
              <Select
                value={month}
                label="Month"
                onChange={(e) => setMonth(Number(e.target.value))}
              >
                {monthNames.map((monthName, index) => (
                  <MenuItem key={monthName} value={index + 1}>
                    {monthName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel>Year</InputLabel>
              <Select
                value={year}
                label="Year"
                onChange={(e) => setYear(Number(e.target.value))}
              >
                {yearOptions.map((optionYear) => (
                  <MenuItem key={optionYear} value={optionYear}>
                    {optionYear}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box>
            <input
              id="offline-cur-file-input"
              type="file"
              accept=".gz,application/gzip"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
            <label htmlFor="offline-cur-file-input">
              <Button component="span" variant="outlined" sx={{ textTransform: "none" }}>
                {selectedFile ? "Change file" : "Choose file"}
              </Button>
            </label>
            <Typography sx={{ mt: 1, fontSize: "0.8125rem", color: "#64748b" }}>
              {selectedFile ? selectedFile.name : "No file selected"}
            </Typography>
          </Box>
          {isUploading && uploadProgress?.total ? (
            <Box sx={{ mt: 1 }}>
              <LinearProgress
                variant="determinate"
                value={Math.min(100, (uploadProgress.completed / uploadProgress.total) * 100)}
                sx={{ borderRadius: 1, height: 8 }}
              />
              <Typography sx={{ mt: 0.75, fontSize: "0.75rem", color: "#64748b" }}>
                Uploading part {uploadProgress.completed} of {uploadProgress.total}…
              </Typography>
            </Box>
          ) : null}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} sx={{ textTransform: "none" }}>
          {isUploading ? "Stop upload" : "Cancel"}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isUploading || !isFormComplete}
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          {isUploading ? "Uploading…" : "Upload"}
        </Button>
      </DialogActions>
    </Dialog>
    {open ? (
      <Paper
        elevation={8}
        sx={{
          position: "fixed",
          top: 32,
          right: 24,
          width: 420,
          maxWidth: "calc(100vw - 32px)",
          height: "calc(100vh - 64px)",
          zIndex: 1400,
          borderRadius: 2,
          border: "1px solid #e5e7eb",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box sx={{ px: 2.25, py: 1.5, backgroundColor: "#f8fafc", borderBottom: "1px solid #e5e7eb" }}>
          <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: "#0f172a" }}>GZIP Compression Guide</Typography>
          <Typography sx={{ fontSize: "0.76rem", color: "#64748b", mt: 0.25 }}>Windows | Linux | macOS</Typography>
        </Box>
        <Box sx={{ p: 2, overflowY: "auto" }}>
          <Typography sx={{ fontSize: "0.83rem", fontWeight: 700, mb: 0.5 }}>1) What is GZIP?</Typography>
          <Typography sx={{ fontSize: "0.78rem", color: "#475569", mb: 0.75 }}>
            GZIP (GNU Zip) is a lossless compression tool used to reduce file size. It creates `.gz` files and is
            commonly used for logs, pipelines, and file transfer.
          </Typography>
          <Box component="ul" sx={{ pl: 2.2, mt: 0.25, mb: 1.25, color: "#475569" }}>
            <Typography component="li" sx={{ fontSize: "0.76rem", mb: 0.3 }}>Compresses one file at a time</Typography>
            <Typography component="li" sx={{ fontSize: "0.76rem", mb: 0.3 }}>No data loss (lossless)</Typography>
            <Typography component="li" sx={{ fontSize: "0.76rem" }}>Standard in backend/data workflows</Typography>
          </Box>

          {OS_SECTIONS.map((section, idx) => (
            <Box key={section.title} sx={{ mb: 1.5 }}>
              <Divider sx={{ mb: 1.25, mt: idx === 0 ? 0.75 : 0 }} />
              <Typography sx={{ fontSize: "0.83rem", fontWeight: 700, mb: 0.75 }}>
                {idx + 2}) GZIP on {section.title}
              </Typography>
              {section.items.map((item) => (
                <Box key={`${section.title}-${item.label}`} sx={{ mb: 0.9 }}>
                  <Typography sx={{ fontSize: "0.74rem", color: "#475569", mb: 0.2 }}>{item.label}</Typography>
                  <CopyableCodeBlock code={item.code} onCopy={handleCopyCode} copied={copiedCode === item.code} />
                </Box>
              ))}
              {section.note ? (
                <Typography sx={{ fontSize: "0.73rem", color: "#64748b", fontStyle: "italic", mt: 0.5 }}>
                  Note: {section.note}
                </Typography>
              ) : null}
            </Box>
          ))}
        </Box>
      </Paper>
    ) : null}
    <Snackbar
      open={copySnackbarOpen}
      autoHideDuration={1600}
      onClose={() => setCopySnackbarOpen(false)}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
    >
      <Alert severity="success" onClose={() => setCopySnackbarOpen(false)}>
        Code copied
      </Alert>
    </Snackbar>
    </>
  );
};

export default UploadOfflineCurModal;
